import { styles } from '../../duck';
import ReturnAssignment from '../return-assignment';
import {
  Divider,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import { getQuery } from '@lon/api';
import { Icon } from '@lon/shared/components';
import { StudentAssignmentStatusTransformed } from '@lon/shared/enums';
import { useThemeStyleValue, useQueryParams } from '@lon/shared/hooks';
import { routes } from '@lon/shared/configs';
import { AssignmentPageColumns } from '@lon/suit/types';

const RowActions: React.FC<{
  rowData: AssignmentPageColumns;
  originalPath?: string
  isLti?: boolean;
}> = ({ rowData, originalPath = routes.classes.studentAssignmentDetails, isLti = false }) => {
  const { t } = useTranslation();
  const [queryParams] = useQueryParams();

  return (
    <Menu
      variant="actions"
      autoSelect={false}
      strategy="fixed"
      placement="bottom-end"
      modifiers={[
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-end', 'left'],
          },
        },
      ]}
    >
      <Tooltip variant="dark" label={t('assignments.rowActionsLTITooltip')}>
        <MenuButton
          as={IconButton}
          size="md"
          icon={<Icon name="pointsOutlined" />}
          variant={useThemeStyleValue('empty', 'ghost')}
          boxShadow="none"
          aria-label={t('assignments.viewActions')}
          _active={{ backgroundColor: 'unset' }}
        >
          <Text variant="bs">{t('assignments.actions')}</Text>
        </MenuButton>
      </Tooltip>
      <MenuList data-focus-visible-disabled>
        {[
          StudentAssignmentStatusTransformed.Graded,
          StudentAssignmentStatusTransformed.Returned,
          StudentAssignmentStatusTransformed.Assigned,
        ].includes(rowData.transformedStatus) && (
          <>
            <Tooltip
              isDisabled={
                rowData.transformedStatus ===
                StudentAssignmentStatusTransformed.Assigned
              }
              variant="dark"
              label={t('assignments.viewAssignmentGradesTooltip', {
                studentsName: `${rowData.firstName} ${rowData.lastName}`,
              })}
            >
              <MenuItem
                p={0}
                isDisabled={
                  rowData.transformedStatus ===
                  StudentAssignmentStatusTransformed.Assigned
                }            
                to={`${generatePath(originalPath, {
                  studentId: rowData.studentId,
                  assignmentId: rowData.assignmentId || '',
                  sessionId: rowData.sessionId || rowData.id,
                })}?${getQuery({ ...queryParams })}`}
                as={Link}
              >
                <Flex flex="1" gap={3} py="2" px="6">
                  <Icon name="eyeDdlOutlined" className={styles.icon} />
                  <Text variant="s2">{t('assignments.viewAssignmentGrades')}</Text>
                </Flex>
              </MenuItem>
            </Tooltip>
            <Divider my={2} />
          </>
        )}
        {rowData.transformedStatus ===
          StudentAssignmentStatusTransformed.ToBeGraded && (
          <>
            <Tooltip
              variant="dark"
              label={t('assignments.gradeAssignmentLTITooltip', {
                studentsName: `${rowData.firstName} ${rowData.lastName}`,
              })}
            >
              <MenuItem
                p={0}
                to={`${generatePath(originalPath, {
                  studentId: rowData.studentId,
                  assignmentId: rowData.assignmentId || '',
                  sessionId: rowData.sessionId || rowData.id,
                })}?${getQuery({ ...queryParams })}`}
                as={Link}
              >
                <Flex flex="1" gap={3} py="2" px="6">
                  <Icon name="eyeDdlOutlined" className={styles.icon} />
                  <Text variant="s2">{t('assignments.gradeAssignment')}</Text>
                </Flex>
              </MenuItem>
            </Tooltip>
            <Divider my={2} />
          </>
        )}
        <ReturnAssignment studentsData={[rowData]} isLti={isLti} />
      </MenuList>
    </Menu>
  );
};

export default RowActions;
